'use client';

import { Loader2 } from 'lucide-react';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { MONTHS_PER_YEAR, SUBSCRIPTION_PRICES } from '../constants';
import { usePricingActions } from '../hooks/use-pricing-actions';
import { PricingPageShell } from './pricing-page-shell';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { InfoTooltip } from '@/components/ui/info-tooltip';
import { Label } from '@/components/ui/label';
import { NumericInput } from '@/components/ui/numeric-input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useSubscriptionStatus } from '@/data/billing/use-subscription-status';
import { useUpdateInstallationSettings } from '@/data/installations/update-installation-settings';
import { useInstallation } from '@/data/installations/use-installation';
import { useInstallationMembersStatistics } from '@/data/installations/use-installation-members-statistics';
import { trackClientEvent } from '@/lib/analytics/posthog-client';
import { countDevelopersWithRecentPrs } from '@/lib/billing/pro-seat-policy';
import { PAID_PLANS } from '@/types/billing-plan';

/**
 * Resume flow UI for paused Stripe subscriptions.
 * Displays seat count + pricing summary and starts the setup-mode checkout session.
 */
export function ResumeView() {
  const searchParams = useSearchParams();
  const installationId = searchParams.get('installation_id');
  const quantityParam = searchParams.get('quantity');
  const normalizedQuantityParam = quantityParam?.trim() ?? '';
  const parsedQuantity = /^\d+$/.test(normalizedQuantityParam) ? Number.parseInt(normalizedQuantityParam, 10) : Number.NaN;
  const defaultQuantity = Number.isFinite(parsedQuantity) && parsedQuantity > 0 ? parsedQuantity : 1;
  const [quantity, setQuantity] = useState<number | null>(defaultQuantity);
  const [autoAssignSeats, setAutoAssignSeats] = useState(true);
  const {
    isLoading,
    setIsLoading,
    startResumeFlow
  } = usePricingActions();
  const parsedInstallationId = Number(installationId) || undefined;
  const {
    data: installation,
    isPending: isInstallationLoading
  } = useInstallation(parsedInstallationId);
  const {
    data: subscriptionStatus
  } = useSubscriptionStatus(Number(installationId) || null, {
    live: true
  });
  const {
    data: membersStatisticsData,
    isPending: isMembersStatisticsLoading
  } = useInstallationMembersStatistics(parsedInstallationId);
  const {
    isPending: isUpdatingAutoAssign,
    mutate: updateInstallationSettings
  } = useUpdateInstallationSettings();
  const distinctMemberPRCount = countDevelopersWithRecentPrs(membersStatisticsData?.memberStatistics);

  // Resume is only available for paid plans; defaulting to team keeps rendering stable
  // while subscription status is still loading or missing expected fields.
  const plan = subscriptionStatus?.paidPlan ?? 'team';
  const billingCycle = subscriptionStatus?.billingInterval === 'year' ? 'yearly' : 'monthly';
  useEffect(() => {
    if (installationId) {
      trackClientEvent('resume_page_viewed', installationId);
    }
  }, [installationId]);
  useEffect(() => {
    if (isUpdatingAutoAssign) {
      return;
    }
    setAutoAssignSeats(installation?.stripeAutomaticallyAssignAndRemoveSeats ?? true);
  }, [installation?.stripeAutomaticallyAssignAndRemoveSeats, isUpdatingAutoAssign]);
  const {
    monthly: monthlyPrice,
    yearly: yearlyPrice
  } = SUBSCRIPTION_PRICES[plan];
  const safeQuantity = typeof quantity === 'number' && Number.isFinite(quantity) ? quantity : 0;
  // Keep order summary arithmetic deterministic even when the input is temporarily empty.
  const subtotalPrice = billingCycle === 'yearly' ? monthlyPrice * safeQuantity * MONTHS_PER_YEAR : monthlyPrice * safeQuantity;
  const annualSavings = (monthlyPrice * MONTHS_PER_YEAR - yearlyPrice) * safeQuantity;
  const totalPrice = billingCycle === 'yearly' ? yearlyPrice * safeQuantity : monthlyPrice * safeQuantity;
  const planName = PAID_PLANS[plan].name;
  const cycleName = billingCycle === 'yearly' ? 'Yearly' : 'Monthly';
  const handleAutoAssignToggle = () => {
    if (!installationId || !installation) {
      return;
    }
    const nextAutoAssignSeats = !autoAssignSeats;
    setAutoAssignSeats(nextAutoAssignSeats);
    updateInstallationSettings({
      installationId: Number(installationId),
      stripeAutomaticallyAssignAndRemoveSeats: nextAutoAssignSeats
    });
  };
  const handleResume = async () => {
    if (!installationId) {
      toast.error('Missing installation. Please go back to settings and select an installation.');
      return;
    }
    if (typeof quantity !== 'number' || !Number.isFinite(quantity) || quantity < 1) {
      toast.error('Quantity must be at least 1.');
      return;
    }
    setIsLoading(true);
    try {
      const redirectUrl = await startResumeFlow({
        installationId: Number.parseInt(installationId, 10),
        quantity,
        autoAssignSeats
      });
      window.location.href = redirectUrl;
    } catch (error) {
      const errorMessage = error instanceof Error && error.message ? error.message : 'Failed to start subscription resume. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  return <PricingPageShell title="Resume your subscription" subtitle={`${planName} plan - ${cycleName} billing`} containerClassName="max-w-lg">
      <div className="rounded-xl border border-border bg-[#101011] p-4 sm:p-8">
        <div className="mb-4">
          <p className="flex items-center gap-1.5 text-base">
            Number of developer seats
            <InfoTooltip content="You can assign these developer seats to any GitHub users. cubic will only review PRs for developers who have a cubic seat assigned to them." />
          </p>
          {isMembersStatisticsLoading ? <Skeleton className="my-[3px] h-4 w-1/2" /> : distinctMemberPRCount > 0 && <p className="text-base text-zinc-400">
                {`${distinctMemberPRCount} ${distinctMemberPRCount === 1 ? 'developer' : 'developers'} opened PRs in the last 30 days`}
              </p>}
        </div>

        <div className="mb-6">
          <NumericInput value={quantity} setValue={setQuantity} min={1} className="mb-4" />
          <div className="flex items-center space-x-2">
            <Checkbox id="auto-assign-seats-resume" checked={autoAssignSeats} onCheckedChange={handleAutoAssignToggle} variant="dark" disabled={isUpdatingAutoAssign || isInstallationLoading || !installation} className="disabled:opacity-100" />
            <Label htmlFor="auto-assign-seats-resume" className="cursor-pointer text-base peer-disabled:opacity-100">
              Auto add seats when you add new developers in GitHub
            </Label>
          </div>
        </div>

        {/* Inline order summary */}
        <div className="mb-6 rounded-lg border border-border bg-background p-4">
          <div className="flex items-center justify-between text-base">
            <span className="text-zinc-400">{`$${monthlyPrice} x ${safeQuantity} ${safeQuantity === 1 ? 'seat' : 'seats'}${billingCycle === 'yearly' ? ' x 12 months' : ''}`}</span>
            <span>{`$${subtotalPrice}`}</span>
          </div>

          {billingCycle === 'yearly' && <div className="mt-2 flex items-center justify-between text-base">
              <span className="text-zinc-400">Annual savings</span>
              <span className="text-emerald-400">{`-$${annualSavings}`}</span>
            </div>}

          <hr className="my-3 border-border" />

          <div className="flex items-center justify-between text-base font-medium">
            <span className="text-zinc-400">Total</span>
            <span>{`$${totalPrice}`}</span>
          </div>

          <div className="mt-2 text-right">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-help text-base text-muted-foreground underline">
                    Promo codes at checkout
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-md p-4">
                  <p className="font-medium">
                    You can apply promo codes in the next step in the Stripe checkout page. Note
                    that we currently don&apos;t support promo codes on yearly plans.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </div>

        <Button variant="default" size="lg" className="w-full" disabled={isLoading || safeQuantity < 1 || isInstallationLoading} onClick={handleResume}>
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Resume subscription
        </Button>
      </div>
    </PricingPageShell>;
}